import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Aperture, AlertTriangle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Reveal } from '@/components/Reveal';
import { SEO } from '@/components/SEO';
import { SkeletonList } from '@/components/SkeletonList';
import { SITE_IMAGES } from '@/lib/siteImages';
import { BLUEPRINT_PAGE } from '@/constants/testIds';
import { goldLinkClass, outlineLinkClass } from './primitives';

const COMPOSING_LINES = [
  'Reading your story...',
  'Scouting light across the DMV...',
  'Sequencing the shot list...',
  'Directing poses for your group...',
  'Choosing the palette...',
  'Framing your deliverables...',
];

/** Initial fetch placeholder. */
export const BlueprintLoadingState = () => (
  <div className="pt-16 min-h-[60vh] bg-[#0A0A0A] px-4 py-20" data-testid={BLUEPRINT_PAGE.root}>
    <div className="mx-auto max-w-4xl space-y-4">
      <Skeleton className="h-10 w-2/3 rounded-xl bg-[#141416]" />
      <Skeleton className="h-64 rounded-2xl bg-[#141416]" />
    </div>
  </div>
);

/** Shown while LXRA Intelligence is still composing the blueprint. */
export const BlueprintComposingState = ({ name }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % COMPOSING_LINES.length), 2200);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="pt-16" data-testid={BLUEPRINT_PAGE.root}>
      <SEO title="Composing Your Blueprint" description="LXRA Intelligence is composing your Creative Blueprint." />
      <section className="relative min-h-[calc(100vh-4rem)] flex items-center lxra-grain overflow-hidden" data-testid={BLUEPRINT_PAGE.generating}>
        <img src={SITE_IMAGES.homeHero} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]/60" />
        <div className="relative z-10 mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-10 text-center">
          <motion.span
            className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-[hsl(var(--lxra-gold))]/50 text-[hsl(var(--lxra-gold))]"
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 9, ease: 'linear' }}
          >
            <Aperture className="h-7 w-7" />
          </motion.span>
          <p className="mt-8 text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))]">LXRA Intelligence</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-3">
            COMPOSING {name ? `${name.split(' ')[0].toUpperCase()}'S` : 'YOUR'} BLUEPRINT
          </h1>
          <div className="h-7 mt-5">
            <AnimatePresence mode="wait">
              <motion.p
                key={COMPOSING_LINES[i]}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.3 }}
                className="text-sm text-[hsl(var(--lxra-muted))]"
              >
                {COMPOSING_LINES[i]}
              </motion.p>
            </AnimatePresence>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
            <SkeletonList count={3} className="h-28 rounded-2xl bg-[#141416]" />
          </div>
          <p className="mt-8 text-xs text-[hsl(var(--lxra-muted))]/70">This usually takes under a minute. You can keep this page open.</p>
        </div>
      </section>
    </div>
  );
};

/** Generation failed or produced no content. */
export const BlueprintFailedState = ({ error }) => (
  <div className="pt-16 min-h-[70vh] bg-[#0A0A0A] flex items-center px-4" data-testid={BLUEPRINT_PAGE.root}>
    <SEO title="Blueprint Unavailable" description="We couldn't finish this Creative Blueprint." />
    <Reveal className="mx-auto max-w-md text-center">
      <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-10" data-testid={BLUEPRINT_PAGE.failed}>
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[hsl(8,55%,58%)]/50 text-[hsl(8,55%,58%)] mb-5">
          <AlertTriangle className="h-5 w-5" />
        </span>
        <h1 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))]">WE HIT A SNAG.</h1>
        <p className="mt-3 text-sm text-[hsl(var(--lxra-muted))] leading-relaxed">
          {error || "We couldn't finish this blueprint."} Your request has been saved and the studio will follow up personally.
        </p>
        <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/plan" data-testid={BLUEPRINT_PAGE.newPlan} className={outlineLinkClass}>Try Again</Link>
          <Link to="/contact" data-testid={BLUEPRINT_PAGE.contact} className={goldLinkClass}>Contact the Studio</Link>
        </div>
      </div>
    </Reveal>
  </div>
);
